import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { startGoDoDesktopInstall } from "../../lib/start-godo-desktop-install";
import { useLocalEngine } from "../../context/LocalEngineContext";

interface DesktopInstallWizardProps {
  open: boolean;
  onClose: () => void;
}

export function DesktopInstallWizard({ open, onClose }: DesktopInstallWizardProps) {
  const { t, i18n } = useTranslation();
  const { online, checking, refresh } = useLocalEngine();
  const lang = i18n.language.startsWith("ar") ? "ar" : "en";
  const [installError, setInstallError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) setInstallError(null);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    void refresh({ forceDiscover: true });
    const fast = setInterval(() => void refresh({ forceDiscover: true }), 2000);
    return () => clearInterval(fast);
  }, [open, refresh]);

  useEffect(() => {
    if (!open || !online) return;
    const id = setTimeout(onClose, 4000);
    return () => clearTimeout(id);
  }, [open, online, onClose]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[210] flex items-center justify-center bg-slate-900/60 p-4 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      dir={lang === "ar" ? "rtl" : "ltr"}
    >
      <div className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-2xl border border-slate-200 bg-white p-6 shadow-2xl">
        {online ? (
          <>
            <div className="mb-3 text-3xl" aria-hidden>
              ✅
            </div>
            <h2 className="text-lg font-bold text-emerald-800">
              {t("local_engine.wizard_done_title", { defaultValue: "تم تفعيل مساعد سطح المكتب" })}
            </h2>
            <p className="mt-2 text-sm text-slate-600">
              {t("local_engine.wizard_done_body", {
                defaultValue: "الخدمة تعمل في الخلفية. ارجع لصفحة البصمة واضغط توصيل.",
              })}
            </p>
          </>
        ) : (
          <>
            <h2 className="text-lg font-bold text-slate-900">
              {t("local_engine.wizard_title_pro", { defaultValue: "تثبيت خدمة Windows (مرة واحدة)" })}
            </h2>
            <p className="mt-4 text-sm leading-7 text-slate-800">
              {t("local_engine.wizard_pro_simple", {
                defaultValue:
                  "① من التحميلات افتح GoDo-Install.cmd → موافقة واحدة على الشاشة الزرقاء → انتظر رسالة النجاح. ② ارجع هنا — GoDo يكمل الربط تلقائياً.",
              })}
            </p>
            <p
              className={`mt-4 rounded-lg px-3 py-2 text-sm font-semibold ${
                online ? "bg-emerald-100 text-emerald-900" : "bg-slate-100 text-slate-700"
              }`}
            >
              {checking
                ? t("local_engine.checking", { defaultValue: "جاري الفحص…" })
                : online
                  ? t("local_engine.online", { defaultValue: "متصل" })
                  : t("local_engine.wizard_not_detected", {
                      defaultValue: "الخدمة غير مكتشفة بعد على هذا الجهاز",
                    })}
            </p>
            {installError && <p className="mt-2 text-sm text-red-700">{installError}</p>}

            {!online && (
              <p className="mt-3 text-xs text-amber-900">
                {t("local_engine.wizard_same_pc", {
                  defaultValue:
                    "يجب أن يكون GoDo مفتوحاً على نفس حاسوب شبكة البصمة. إن استمرت المشكلة اتصل بالدعم — لا حاجة لأوامر يدوية.",
                })}
              </p>
            )}
          </>
        )}

        <div className="mt-6 flex flex-wrap gap-2">
          {!online && (
            <button
              type="button"
              className="erp-btn erp-btn-primary"
              onClick={() => {
                setInstallError(null);
                void startGoDoDesktopInstall(window.location.origin).catch((e) =>
                  setInstallError(e instanceof Error ? e.message : String(e)),
                );
              }}
            >
              {t("local_engine.install_btn", { defaultValue: "تنزيل GoDo-Install" })}
            </button>
          )}
          <button type="button" className="erp-btn" onClick={() => void refresh({ forceDiscover: true })}>
            {t("local_engine.recheck", { defaultValue: "إعادة فحص" })}
          </button>
          <button type="button" className="erp-btn erp-btn-ghost" onClick={onClose}>
            {t("common.close", { defaultValue: "إغلاق" })}
          </button>
        </div>
      </div>
    </div>
  );
}
